import { useEffect } from "react"
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext"
import { $isHeadingNode } from "@lexical/rich-text"
import {
  $getRoot,
  $getSelection,
  $isElementNode,
  $isParagraphNode,
  $isRangeSelection,
  LexicalEditor,
  LexicalNode,
  RangeSelection,
} from "lexical"

import styles from "./styles.module.css"

export default function BlockPlaceholderPlugin({ placeholder }: { placeholder?: string }): null {
  const [editor] = useLexicalComposerContext()

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const selection = $getSelection()
        if (!$isRangeSelection(selection)) return

        clearAllPlaceholders(editor)

        // Default to placeholder passed to <RichTextPlugin>
        const allBlocks = getAllTopLevelNodes(editor)
        if (allBlocks.length === 1) return

        setPlaceholderOnSelectedNode(selection, editor, placeholder)
      })
    })
  }, [editor, placeholder])

  return null
}

function getAllTopLevelNodes(editor: LexicalEditor) {
  return editor.getEditorState().read(() => $getRoot().getChildren())
}

function clearAllPlaceholders(editor: LexicalEditor) {
  const topLevelBlocks = getAllTopLevelNodes(editor)
  topLevelBlocks.forEach((block) => {
    const domElem = editor.getElementByKey(block.getKey())
    if (domElem) {
      domElem.classList.remove(styles.nodePlaceholder)
      domElem.classList.remove(styles.headingPlaceholderH1)
      domElem.classList.remove(styles.headingPlaceholderH2)
      domElem.classList.remove(styles.headingPlaceholderH3)
      domElem.removeAttribute("data-placeholder")
    }
  })
}

function setPlaceholderOnSelectedNode(selection: RangeSelection, editor: LexicalEditor, placeholder?: string) {
  const anchorNode = selection.anchor.getNode()

  // Walk up to the top-level element (direct child of root)
  const topLevel = editor.getEditorState().read(() => {
    let n: typeof anchorNode | null = anchorNode
    const root = $getRoot()
    while (n && n.getParent() && n.getParent() !== root) {
      n = n.getParent()
    }
    return n && $isElementNode(n) ? n : null
  })

  if (!topLevel) return

  const isEmpty = topLevel.getTextContent().trim().length === 0
  if (!isEmpty) return

  const placeholderText = getNodePlaceholder(topLevel, placeholder)
  if (!placeholderText) return

  const domElem = editor.getElementByKey(topLevel.getKey())
  if (domElem) {
    domElem.classList.add(styles.nodePlaceholder)
    if ($isHeadingNode(topLevel)) {
      const tag = topLevel.getTag()
      if (tag === "h1") {
        domElem.classList.add(styles.headingPlaceholderH1)
      } else if (tag === "h2") {
        domElem.classList.add(styles.headingPlaceholderH2)
      } else if (tag === "h3") {
        domElem.classList.add(styles.headingPlaceholderH3)
      }
    }
    domElem.setAttribute("data-placeholder", placeholderText)
  }
}

function getNodePlaceholder(node: LexicalNode, placeholder?: string): string | null {
  if ($isHeadingNode(node)) {
    const tag = node.getTag() // e.g., "h1", "h2" ...
    const level = tag ? tag.replace("h", "") : ""
    return `Heading ${level}`.trim()
  }

  if ($isParagraphNode(node)) {
    return placeholder || null
  }

  return null
}
